import React, { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import { trackPropertySearch, trackAddressSelection } from '../utils/analytics';

interface IdealPostcodesSuggestion {
  suggestion: string;
  udprn: number;
}

interface IdealPostcodesAddress {
  line_1: string;
  line_2?: string;
  line_3?: string;
  post_town: string;
  postcode: string;
  building_name?: string;
  building_number?: string;
  thoroughfare?: string;
  locality?: string;
  county?: string;
  district?: string;
}

interface Property {
  fullAddress: string;
  udprn: string;
}

interface AddressSearchInputProps {
  placeholder: string;
  onSelectAddress: (property: Property) => void;
  initialAddress?: string;
  className?: string;
}

export default function AddressSearchInput({ 
  placeholder, 
  onSelectAddress, 
  initialAddress = '',
  className = ''
}: AddressSearchInputProps) {
  const [address, setAddress] = useState(initialAddress);
  const [suggestions, setSuggestions] = useState<IdealPostcodesSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddress = async (query: string) => {
    setError(null);
    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);

    try {
      const apiKey = import.meta.env.VITE_IDEALPOSTCODES_API_KEY;
      const encodedQuery = encodeURIComponent(query.trim());
      const response = await fetch(`https://api.ideal-postcodes.co.uk/v1/autocomplete/addresses?query=${encodedQuery}&api_key=${apiKey}&limit=10&radius=2000`);
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.result && data.result.hits && Array.isArray(data.result.hits)) {
        setSuggestions(data.result.hits);
        setShowSuggestions(data.result.hits.length > 0);
        
        // Track property search
        trackPropertySearch(query, data.result.hits.length);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
        trackPropertySearch(query, 0);
      }
      
    } catch (err) {
      console.error('Address search failed:', err);
      setError('Address lookup failed. Please try again.');
      setSuggestions([]);
      setShowSuggestions(false);
      trackPropertySearch(query, 0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddress(value);
    
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Wait 1 second before making API call
    searchTimeout.current = setTimeout(() => {
      searchAddress(value);
    }, 1000);
  };

  const handleSelectAddress = async (selectedSuggestion: IdealPostcodesSuggestion) => {
    console.log('Selected suggestion:', selectedSuggestion);
    
    try {
      // Get full address details using UDPRN
      const apiKey = import.meta.env.VITE_IDEALPOSTCODES_API_KEY;
      const response = await fetch(`https://api.ideal-postcodes.co.uk/v1/udprn/${selectedSuggestion.udprn}?api_key=${apiKey}`);
      
      if (!response.ok) {
        throw new Error(`Failed to get address details: ${response.status}`);
      }
      
      const data = await response.json();
      const addressData: IdealPostcodesAddress = data.result;
      
      console.log('Full address data:', addressData);
      
      // Construct comprehensive full address using all available address lines
      const fullAddress = [
        addressData.line_1,
        addressData.line_2,
        addressData.line_3,
        addressData.post_town,
        addressData.postcode
      ].filter(part => part && part.trim() !== '').join(', ');
      
      console.log('Full address constructed:', fullAddress);

      // Create simplified Property object with full address and UDPRN
      const mappedProperty: Property = {
        fullAddress: fullAddress,
        udprn: selectedSuggestion.udprn.toString()
      };

      setAddress(fullAddress);
      setShowSuggestions(false);
      
      // Track address selection - determine source from current location
      const currentPath = window.location.pathname;
      const source = currentPath === '/leave-review' ? 'leave_review' : 'homepage';
      trackAddressSelection(fullAddress, selectedSuggestion.udprn.toString(), source);
      
      onSelectAddress(mappedProperty);
      
    } catch (error) {
      console.error('Error getting full address:', error);
      // Fallback to using just the suggestion
      const fallbackProperty: Property = {
        fullAddress: selectedSuggestion.suggestion,
        udprn: selectedSuggestion.udprn.toString()
      };
      
      setAddress(selectedSuggestion.suggestion);
      setShowSuggestions(false);
      
      // Track address selection with fallback
      const currentPath = window.location.pathname;
      const source = currentPath === '/leave-review' ? 'leave_review' : 'homepage';
      trackAddressSelection(selectedSuggestion.suggestion, selectedSuggestion.udprn.toString(), source);
      
      onSelectAddress(fallbackProperty);
    }
  };

  return (
    <div className={`relative w-full max-w-2xl ${className}`}>
      <input
        type="text"
        value={address}
        onChange={handleAddressChange}
        placeholder={placeholder}
        className="w-full p-4 pr-12 rounded-lg bg-white text-gray-900 placeholder:text-gray-600 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#30a86b] focus:border-transparent hidden"
      />
      <input
        type="text"
        value={address}
        onChange={handleAddressChange}
        placeholder="Search for a property..."
        className="w-full p-4 pr-12 rounded-lg bg-white text-gray-900 placeholder:text-gray-600 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#30a86b] focus:border-transparent sm:hidden"
      />
      <input
        type="text"
        value={address}
        onChange={handleAddressChange}
        placeholder={placeholder}
        className="w-full p-4 pr-12 rounded-lg bg-white text-gray-900 placeholder:text-gray-600 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#30a86b] focus:border-transparent hidden sm:block"
      />
      <button 
        onClick={() => searchAddress(address)}
        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-[#30a86b] rounded-md hover:bg-[#2a9760] transition-colors"
        disabled={isLoading}
      >
        <Search className="w-5 h-5 text-white" />
      </button>
      
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
              onClick={() => handleSelectAddress(suggestion)}
            >
              <div className="font-medium text-gray-900">
                {suggestion.suggestion}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {isLoading && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
          <div className="text-gray-600 text-center flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#30a86b]"></div>
            Searching addresses...
          </div>
        </div>
      )}
      
      {error && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-red-300 rounded-lg shadow-lg p-3">
          <div className="text-red-600 text-center text-sm">{error}</div>
        </div>
      )}
    </div>
  );
}